import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getAdminDoctors,
  getIncompleteDoctors,
  verifyDoctor,
  createAdminDoctor,
  updateAdminDoctor,
  deleteAdminDoctor,
  uploadDoctorSignature,
  getDoctorSignatureUrl,
  deleteDoctorSignature,
  getDoctorRegistrationSetting,
  setDoctorRegistrationSetting,
} from '../../../services/adminService';
import { invalidateDoctorRegistrationCache } from '../../../hooks/useDoctorRegistrationOpen';
import { prepareSignatureImage } from '../../../utils/signatureImage';
import {
  Search, Filter, CheckCircle2, XCircle, ChevronLeft,
  ChevronRight, RefreshCw, Star, Stethoscope, Phone,
  BadgeCheck, AlertCircle, X, CreditCard, UserPlus,
  UserX, AlertTriangle, Pencil, Trash2, Check, PenLine, Upload,
} from 'lucide-react';

// ─── helpers ────────────────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fullName = (u) => u?.profile ? `${u.profile.firstName} ${u.profile.lastName}`.trim() : u?.email || '—';

const SPECS = [
  'Medicina General','Pediatría','Cardiología','Dermatología',
  'Ginecología','Psiquiatría','Neurología','Oftalmología','Otra',
];

const VerifiedBadge = ({ v }) => v
  ? <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700"><BadgeCheck className="w-3 h-3" />Verificado</span>
  : <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700"><AlertCircle className="w-3 h-3" />Pendiente</span>;

// ─── Create / Edit Doctor Modal ──────────────────────────────────────────────
const DoctorFormModal = ({ onClose, onSaved, prefillUser, editDoctor }) => {
  const isEdit    = !!editDoctor;
  const isPrefill = !!prefillUser;

  const [form, setForm] = useState(() => {
    if (isEdit) {
      const u = editDoctor.user || {};
      return {
        firstName:         u.profile?.firstName        || '',
        lastName:          u.profile?.lastName         || '',
        email:             u.email                     || '',
        password:          '',
        phone:             u.profile?.phone            || '',
        specialization:    editDoctor.specialization   || '',
        licenseNumber:     editDoctor.licenseNumber    || '',
        yearsOfExperience: editDoctor.yearsOfExperience ?? '',
        bio:               editDoctor.bio              || '',
        isVerified:        editDoctor.isVerified       || false,
      };
    }
    return {
      firstName:         prefillUser?.profile?.firstName || '',
      lastName:          prefillUser?.profile?.lastName  || '',
      email:             prefillUser?.email              || '',
      password:          '',
      phone:             prefillUser?.profile?.phone     || '',
      specialization:    '',
      licenseNumber:     '',
      yearsOfExperience: '',
      bio:               '',
      isVerified:        false,
    };
  });

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !isPrefill && form.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    setSaving(true); setError(null);
    try {
      let result;
      if (isEdit) {
        const payload = {
          firstName:         form.firstName.trim(),
          lastName:          form.lastName.trim(),
          phone:             form.phone.trim(),
          specialization:    form.specialization,
          licenseNumber:     form.licenseNumber.trim(),
          yearsOfExperience: form.yearsOfExperience !== '' ? Number(form.yearsOfExperience) : undefined,
          bio:               form.bio.trim() || undefined,
          isVerified:        form.isVerified,
        };
        result = await updateAdminDoctor(editDoctor._id, payload);
      } else if (isPrefill) {
        const payload = {
          prefillUserId:     prefillUser._id,
          phone:             form.phone.trim(),
          specialization:    form.specialization,
          licenseNumber:     form.licenseNumber.trim(),
          yearsOfExperience: form.yearsOfExperience !== '' ? Number(form.yearsOfExperience) : 0,
          bio:               form.bio.trim() || undefined,
          isVerified:        form.isVerified,
        };
        result = await createAdminDoctor(payload);
      } else {
        const payload = {
          email:             form.email.trim(),
          password:          form.password,
          firstName:         form.firstName.trim(),
          lastName:          form.lastName.trim(),
          phone:             form.phone.trim(),
          specialization:    form.specialization,
          licenseNumber:     form.licenseNumber.trim(),
          yearsOfExperience: form.yearsOfExperience !== '' ? Number(form.yearsOfExperience) : 0,
          bio:               form.bio.trim() || undefined,
          isVerified:        form.isVerified,
        };
        result = await createAdminDoctor(payload);
      }
      onSaved(result, isEdit);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Error al guardar médico');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400";

  const title = isEdit ? 'Editar médico' : isPrefill ? 'Completar perfil' : 'Agregar médico';
  const icon  = isEdit ? <Pencil className="w-4 h-4 text-teal-600" /> : <UserPlus className="w-4 h-4 text-teal-600" />;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">{icon}</div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">{title}</h2>
              {isPrefill && !isEdit && (
                <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />Completando perfil de {prefillUser.email}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Datos personales */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Datos personales</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Nombre *</label>
                <input className={inputCls} value={form.firstName} onChange={e => set('firstName', e.target.value)} required={!isPrefill} disabled={isPrefill && !!form.firstName && !isEdit} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Apellido *</label>
                <input className={inputCls} value={form.lastName} onChange={e => set('lastName', e.target.value)} required={!isPrefill} disabled={isPrefill && !!form.lastName && !isEdit} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Email</label>
                <input type="email" className={inputCls} value={form.email} disabled />
              </div>
              {!isEdit && !isPrefill && (
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Contraseña * (mín. 8 caracteres)</label>
                  <input type="password" className={inputCls} value={form.password} onChange={e => set('password', e.target.value)} required placeholder="Mínimo 8 caracteres" />
                  <p className="mt-0.5 text-xs text-gray-400">El médico deberá cambiarla en su primer acceso.</p>
                </div>
              )}
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Teléfono</label>
                <input className={inputCls} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+57 300 000 0000" />
              </div>
            </div>
          </div>

          {/* Datos profesionales */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Datos profesionales</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Especialización *</label>
                <select className={inputCls} value={form.specialization} onChange={e => set('specialization', e.target.value)} required>
                  <option value="">Seleccionar especialización</option>
                  {SPECS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">N° de licencia *</label>
                <input className={inputCls} value={form.licenseNumber} onChange={e => set('licenseNumber', e.target.value)} required placeholder="RM-12345" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Años de experiencia</label>
                <input type="number" min="0" className={inputCls} value={form.yearsOfExperience} onChange={e => set('yearsOfExperience', e.target.value)} placeholder="5" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Biografía</label>
                <textarea rows={3} maxLength={500} className={inputCls} value={form.bio} onChange={e => set('bio', e.target.value)}
                  placeholder="Breve descripción del médico (máx. 500 caracteres)" />
              </div>
            </div>
          </div>

          {/* Verificación */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
            <input
              id="isVerified"
              type="checkbox"
              className="w-4 h-4 accent-teal-600"
              checked={form.isVerified}
              onChange={e => set('isVerified', e.target.checked)}
            />
            <label htmlFor="isVerified" className="text-sm text-gray-700 cursor-pointer">
              Marcar como <span className="font-medium text-teal-700">verificado</span>
            </label>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors">
            {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : isEdit ? <Pencil className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
            {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : isPrefill ? 'Completar perfil' : 'Crear médico'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Delete Confirm Modal ────────────────────────────────────────────────────
const DeleteConfirmModal = ({ doctor, onClose, onConfirm, loading }) => {
  const name = fullName(doctor.user || {});
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex flex-col items-center text-center gap-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">¿Desactivar médico?</h3>
            <p className="text-sm text-gray-500 mt-1">
              Se desactivará la cuenta de <strong>{name}</strong>. El médico no podrá iniciar sesión ni recibir citas.
              Esta acción es reversible.
            </p>
          </div>
          <div className="flex items-center gap-3 w-full">
            <button onClick={onClose} className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
              {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Desactivar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Firma del médico ────────────────────────────────────────────────────────
// La firma se estampa en los PDFs de historia clínica junto al nombre y el
// registro médico. Se carga aquí, en el mismo lugar donde se verifica al médico.
const SignatureManager = ({ doctor, onChanged }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [hasSignature, setHasSignature] = useState(!!doctor.signature?.hasSignature);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(doctor.signature?.updatedAt || null);
  const fileInputRef = useRef(null);
  // Los object URL se liberan al desmontar y al reemplazar la imagen.
  const previewUrlRef = useRef(null);

  const setPreview = useCallback((url) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = url;
    setPreviewUrl(url);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!doctor.signature?.hasSignature) {
      setPreview(null);
      setHasSignature(false);
      return () => { cancelled = true; };
    }
    getDoctorSignatureUrl(doctor._id)
      .then((url) => {
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        setPreview(url);
        setHasSignature(!!url);
      })
      .catch(() => { if (!cancelled) setError('No se pudo cargar la firma registrada'); });
    return () => { cancelled = true; };
  }, [doctor._id, doctor.signature?.hasSignature, setPreview]);

  // Liberar el último object URL al desmontar.
  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // permite volver a elegir el mismo archivo
    if (!file) return;

    setBusy(true); setError(null);
    try {
      const { dataUrl } = await prepareSignatureImage(file);
      const saved = await uploadDoctorSignature(doctor._id, dataUrl);
      setPreview(dataUrl);          // dataUrl no es object URL: revoke lo ignora
      setHasSignature(true);
      setUpdatedAt(saved?.updatedAt || new Date().toISOString());
      onChanged?.(doctor._id, true);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'No se pudo guardar la firma');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`¿Eliminar la firma de ${fullName(doctor.user)}? Las historias clínicas nuevas saldrán sin firma.`)) return;
    setBusy(true); setError(null);
    try {
      await deleteDoctorSignature(doctor._id);
      setPreview(null);
      setHasSignature(false);
      setUpdatedAt(null);
      onChanged?.(doctor._id, false);
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo eliminar la firma');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 rounded-xl bg-gray-50">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
            <PenLine className="w-3 h-3" />Firma para historias clínicas
          </p>
          {hasSignature
            ? <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                <Check className="w-3 h-3" />Registrada
              </span>
            : <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                <AlertCircle className="w-3 h-3" />Sin firma
              </span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
          >
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {hasSignature ? 'Reemplazar' : 'Subir firma'}
          </button>
          {hasSignature && (
            <button
              onClick={handleDelete}
              disabled={busy}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" />Quitar
            </button>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={handleFile}
      />

      {previewUrl ? (
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center justify-center">
          <img src={previewUrl} alt="Firma del médico" className="max-h-20 object-contain" />
        </div>
      ) : (
        <div className="bg-white border border-dashed border-gray-300 rounded-lg p-4 text-center">
          <p className="text-xs text-gray-400">
            Sube una imagen PNG o JPEG del trazo de la firma, preferiblemente con fondo transparente.
          </p>
        </div>
      )}

      {updatedAt && (
        <p className="text-xs text-gray-400 mt-2">Actualizada el {fmtDate(updatedAt)}</p>
      )}
      {error && (
        <p className="text-xs text-red-600 mt-2 flex items-start gap-1">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />{error}
        </p>
      )}
    </div>
  );
};

// ─── Detail Drawer ───────────────────────────────────────────────────────────
const DoctorDrawer = ({ doctor, onClose, onVerifyToggle, onEdit, onSignatureChanged, loading }) => {
  if (!doctor) return null;
  const u    = doctor.user || {};
  const name = fullName(u);
  const DAYS_ES = { monday:'Lun', tuesday:'Mar', wednesday:'Mié', thursday:'Jue', friday:'Vie', saturday:'Sáb', sunday:'Dom' };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-xl flex flex-col overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Detalle del médico</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => onEdit(doctor)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-teal-50 text-teal-700 hover:bg-teal-100 rounded-lg font-medium">
              <Pencil className="w-3.5 h-3.5" />Editar
            </button>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="flex-1 px-6 py-5 space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
              <Stethoscope className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">{name}</p>
              <p className="text-sm text-gray-500">{u.email}</p>
              {u.profile?.phone && (
                <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" />{u.profile.phone}</p>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
            <div>
              <p className="text-xs text-gray-500 mb-1">Estado de verificación</p>
              <VerifiedBadge v={doctor.isVerified} />
            </div>
            <button
              onClick={() => onVerifyToggle(doctor._id, !doctor.isVerified)}
              disabled={loading}
              className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
                doctor.isVerified ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-teal-600 text-white hover:bg-teal-700'
              } disabled:opacity-50`}
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : doctor.isVerified ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
              {doctor.isVerified ? 'Quitar verificación' : 'Verificar médico'}
            </button>
          </div>
          <SignatureManager doctor={doctor} onChanged={onSignatureChanged} />
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Especialización',    value: doctor.specialization },
              { label: 'Licencia',           value: doctor.licenseNumber },
              { label: 'Experiencia',        value: doctor.yearsOfExperience != null ? `${doctor.yearsOfExperience} años` : '—' },
              { label: 'Calificación',       value: doctor.rating?.count > 0 ? `${doctor.rating.average.toFixed(1)} ★ (${doctor.rating.count})` : 'Sin calificaciones' },
              { label: 'Consultas totales',  value: doctor.totalConsultations ?? 0 },
              { label: 'Miembro desde',      value: fmtDate(doctor.createdAt) },
              { label: 'Cuenta activa',      value: u.isActive ? 'Sí' : 'No' },
            ].map(({ label, value }) => (
              <div key={label} className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                <p className="text-sm font-medium text-gray-800">{value}</p>
              </div>
            ))}
          </div>
          {doctor.bio && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Biografía</p>
              <p className="text-sm text-gray-700 leading-relaxed">{doctor.bio}</p>
            </div>
          )}
          {doctor.education?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Educación</p>
              <div className="space-y-2">
                {doctor.education.map((e, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <CreditCard className="w-3.5 h-3.5 text-gray-300 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{e.degree} — {e.institution} {e.year ? `(${e.year})` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {doctor.availability && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Disponibilidad</p>
              <div className="grid grid-cols-7 gap-1">
                {Object.entries(DAYS_ES).map(([key, label]) => {
                  const day = doctor.availability[key];
                  return (
                    <div key={key} className={`rounded-lg p-2 text-center ${
                      day?.available ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-300'
                    }`}>
                      <p className="text-xs font-medium">{label}</p>
                      {day?.available && day.start && <p className="text-xs mt-0.5 leading-none">{day.start}</p>}
                      {day?.available && day.start2 && <p className="text-xs mt-0.5 leading-none">{day.start2}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Tab: Médicos Incompletos ─────────────────────────────────────────────────
const IncompleteTab = ({ onCompleted }) => {
  const [users,      setUsers]      = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [search,     setSearch]     = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page,       setPage]       = useState(1);
  const [completing, setCompleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data, pagination: pg } = await getIncompleteDoctors({ page, limit: 10, search });
      setUsers(data);
      setPagination(pg);
    } catch (e) {
      setError(e?.response?.data?.message || 'Error al cargar médicos incompletos');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => { e.preventDefault(); setSearch(searchInput.trim()); setPage(1); };

  const handleCompleted = (newDoctor) => {
    setUsers(prev => prev.filter(u => u._id !== newDoctor.user?._id && u._id !== newDoctor.user));
    setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    setCompleting(null);
    onCompleted(newDoctor);
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSearch} className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
            placeholder="Buscar por nombre o email…"
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </div>
        <button type="submit" className="px-3 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700">Buscar</button>
        <button type="button" onClick={load} disabled={loading}
          className="flex items-center gap-1.5 text-sm text-gray-500 border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </form>

      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-700">
          Estos usuarios se registraron con rol <strong>médico</strong> pero nunca completaron su perfil profesional.
          Usa <strong>Completar perfil</strong> para que aparezcan en la plataforma.
        </p>
      </div>

      {error ? (
        <div className="flex flex-col items-center justify-center h-40 gap-3 text-red-500">
          <AlertCircle className="w-8 h-8" />
          <p className="text-sm">{error}</p>
          <button onClick={load} className="text-sm text-teal-600 hover:underline">Reintentar</button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="animate-pulse p-4 space-y-3">
              {[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded-lg" />)}
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <UserX className="w-10 h-10 mb-3 text-gray-200" />
              <p className="text-sm font-medium text-gray-500">¡Todo en orden!</p>
              <p className="text-xs mt-1">No hay médicos con perfil incompleto</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>{['Médico','Teléfono','Cuenta activa','Registro',''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map(u => (
                    <tr key={u._id} className="hover:bg-amber-50/40">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{fullName(u)}</p>
                        <p className="text-xs text-gray-400">{u.email}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{u.profile?.phone || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                          u.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                        }`}>{u.isActive ? 'Activa' : 'Inactiva'}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">{fmtDate(u.createdAt)}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => setCompleting(u)}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-teal-50 text-teal-700 hover:bg-teal-100 rounded-lg font-medium transition-colors">
                          <UserPlus className="w-3.5 h-3.5" />Completar perfil
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">Página {pagination.page} de {pagination.pages} · {pagination.total} sin completar</p>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => p - 1)} disabled={page <= 1 || loading}
              className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= pagination.pages || loading}
              className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {completing && (
        <DoctorFormModal
          prefillUser={completing}
          onClose={() => setCompleting(null)}
          onSaved={handleCompleted}
        />
      )}
    </div>
  );
};

// ─── Main DoctorsSection ─────────────────────────────────────────────────────
// ─── Interruptor del registro público de médicos ─────────────────────────────
// Cerrado: el formulario de registro no ofrece "Soy médico", la landing
// /medicos y sus enlaces se ocultan, y el backend rechaza el registro con 403.
// Los médicos se siguen dando de alta con "Agregar médico".
const DoctorRegistrationToggle = () => {
  const [open,    setOpen]    = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    getDoctorRegistrationSetting()
      .then(setOpen)
      .catch(() => setError('No se pudo leer el estado del registro'));
  }, []);

  const toggle = async () => {
    setSaving(true); setError(null);
    try {
      const next = await setDoctorRegistrationSetting(!open);
      setOpen(next);
      invalidateDoctorRegistrationCache();
    } catch (e) {
      setError(e?.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const loaded = open !== null;

  return (
    <div className={`flex items-center justify-between gap-4 rounded-xl border px-4 py-3 ${
      open ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'
    }`}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">
          Registro público de médicos: {!loaded ? '…' : open ? 'abierto' : 'cerrado'}
        </p>
        <p className="text-xs text-gray-500">
          {open
            ? 'Cualquier médico puede crear su cuenta desde el formulario de registro y la página /medicos.'
            : 'El formulario solo registra pacientes y la página /medicos está oculta. Puedes seguir agregando médicos desde aquí.'}
        </p>
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={!!open}
        aria-label="Registro público de médicos"
        onClick={toggle}
        disabled={!loaded || saving}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
          open ? 'bg-teal-600' : 'bg-gray-300'
        }`}>
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          open ? 'translate-x-5' : 'translate-x-0.5'
        }`} />
      </button>
    </div>
  );
};

export default function DoctorsSection() {
  const [tab,        setTab]        = useState('registered');
  const [doctors,    setDoctors]    = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [search,     setSearch]     = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterVerified, setFilterVerified] = useState('');
  const [filterSpec,     setFilterSpec]     = useState('');
  const [page,       setPage]       = useState(1);
  const [selected,   setSelected]   = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCreate,    setShowCreate]    = useState(false);
  const [editDoctor,    setEditDoctor]    = useState(null);
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [incompleteCount, setIncompleteCount] = useState(null);

  useEffect(() => {
    getIncompleteDoctors({ page: 1, limit: 1 })
      .then(({ pagination: pg }) => setIncompleteCount(pg.total))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { page, limit: 10 };
      if (filterVerified !== '') params.verified = filterVerified;
      if (search)               params.search   = search;
      if (filterSpec)           params.specialization = filterSpec;
      const { data, pagination: pg } = await getAdminDoctors(params);
      setDoctors(data);
      setPagination(pg);
    } catch (e) {
      setError(e?.response?.data?.message || 'Error al cargar médicos');
    } finally {
      setLoading(false);
    }
  }, [page, filterVerified, search, filterSpec]);

  useEffect(() => { if (tab === 'registered') load(); }, [load, tab]);

  const handleVerifyToggle = async (doctorId, newValue) => {
    // Aviso no bloqueante: un médico verificado sin firma genera historias
    // clínicas sin firma manuscrita en el PDF.
    if (newValue) {
      const doctor = doctors.find(d => d._id === doctorId) || (selected?._id === doctorId ? selected : null);
      if (doctor && !doctor.signature?.hasSignature) {
        const proceed = window.confirm(
          'Este médico no tiene firma registrada. Si lo verificas ahora, sus historias clínicas ' +
          'saldrán sin firma en el PDF (solo con el nombre y el registro médico).\n\n¿Continuar?'
        );
        if (!proceed) return;
      }
    }

    setActionLoading(true);
    try {
      await verifyDoctor(doctorId, newValue);
      setDoctors(prev => prev.map(d => d._id === doctorId ? { ...d, isVerified: newValue } : d));
      if (selected?._id === doctorId) setSelected(prev => ({ ...prev, isVerified: newValue }));
    } catch (e) {
      alert(e?.response?.data?.message || 'Error al cambiar verificación');
    } finally {
      setActionLoading(false);
    }
  };

  /** Mantiene sincronizado el estado de la firma en la lista y en el drawer. */
  const handleSignatureChanged = (doctorId, hasSignature) => {
    const patch = (d) => ({
      ...d,
      signature: { ...(d.signature || {}), hasSignature, updatedAt: hasSignature ? new Date().toISOString() : null },
    });
    setDoctors(prev => prev.map(d => (d._id === doctorId ? patch(d) : d)));
    if (selected?._id === doctorId) setSelected(prev => patch(prev));
  };

  const handleSaved = (savedDoctor, isEdit) => {
    if (isEdit) {
      setDoctors(prev => prev.map(d => d._id === savedDoctor._id ? savedDoctor : d));
      if (selected?._id === savedDoctor._id) setSelected(savedDoctor);
    } else {
      setDoctors(prev => [savedDoctor, ...prev]);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await deleteAdminDoctor(deleteTarget._id);
      setDoctors(prev => prev.filter(d => d._id !== deleteTarget._id));
      setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      if (selected?._id === deleteTarget._id) setSelected(null);
      setDeleteTarget(null);
    } catch (e) {
      alert(e?.response?.data?.message || 'Error al desactivar médico');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSearch = (e) => { e.preventDefault(); setSearch(searchInput.trim()); setPage(1); };

  const handleIncompleteCompleted = (newDoctor) => {
    setDoctors(prev => [newDoctor, ...prev]);
    setIncompleteCount(c => Math.max(0, (c ?? 1) - 1));
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Gestión de Médicos</h2>
          <p className="text-sm text-gray-500">{pagination.total} médicos registrados</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading || tab !== 'registered'}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-teal-600 border border-gray-200 rounded-lg px-3 py-1.5 hover:border-teal-300 transition-colors disabled:opacity-40">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Actualizar
          </button>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 text-sm bg-teal-600 text-white px-4 py-1.5 rounded-lg hover:bg-teal-700 transition-colors">
            <UserPlus className="w-4 h-4" />Agregar médico
          </button>
        </div>
      </div>

      <DoctorRegistrationToggle />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        <button onClick={() => setTab('registered')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            tab === 'registered' ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}>
          <Stethoscope className="w-4 h-4" />Médicos registrados
        </button>
        <button onClick={() => setTab('incomplete')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            tab === 'incomplete' ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}>
          <AlertTriangle className="w-4 h-4" />
          Perfil incompleto
          {incompleteCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-amber-500 text-white rounded-full">
              {incompleteCount > 99 ? '99+' : incompleteCount}
            </span>
          )}
        </button>
      </div>

      {/* Tab: Médicos registrados */}
      {tab === 'registered' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 min-w-48">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
                  placeholder="Buscar por nombre o email…"
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
              </div>
              <button type="submit" className="px-3 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700">Buscar</button>
            </form>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select value={filterVerified} onChange={e => { setFilterVerified(e.target.value); setPage(1); }}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500">
                <option value="">Todos</option>
                <option value="true">Verificados</option>
                <option value="false">Pendientes</option>
              </select>
              <select value={filterSpec} onChange={e => { setFilterSpec(e.target.value); setPage(1); }}
                className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500">
                <option value="">Todas las especialidades</option>
                {SPECS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Table */}
          {error ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3 text-red-500">
              <AlertCircle className="w-8 h-8" />
              <p className="text-sm">{error}</p>
              <button onClick={load} className="text-sm text-teal-600 hover:underline">Reintentar</button>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="animate-pulse p-4 space-y-3">
                  {[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded-lg" />)}
                </div>
              ) : doctors.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <Stethoscope className="w-10 h-10 mb-3 text-gray-200" />
                  <p className="text-sm">No se encontraron médicos</p>
                  {(search || filterVerified || filterSpec) && (
                    <button onClick={() => { setSearch(''); setSearchInput(''); setFilterVerified(''); setFilterSpec(''); setPage(1); }}
                      className="mt-2 text-sm text-teal-600 hover:underline">Limpiar filtros</button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {['Médico','Especialidad','Licencia','Rating','Estado',''].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {doctors.map(doc => {
                        const u    = doc.user || {};
                        const name = fullName(u);
                        return (
                          <tr key={doc._id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-900">{name}</p>
                              <p className="text-xs text-gray-400">{u.email}</p>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{doc.specialization}</td>
                            <td className="px-4 py-3 text-gray-500 font-mono text-xs">{doc.licenseNumber}</td>
                            <td className="px-4 py-3">
                              {doc.rating?.count > 0
                                ? <span className="flex items-center gap-1 text-yellow-500 font-medium"><Star className="w-3.5 h-3.5" />{doc.rating.average.toFixed(1)}</span>
                                : <span className="text-gray-300 text-xs">Sin votos</span>}
                            </td>
                            <td className="px-4 py-3">
                              <VerifiedBadge v={doc.isVerified} />
                              {!doc.signature?.hasSignature && (
                                <span
                                  className="mt-1 inline-flex items-center gap-1 text-xs text-gray-400"
                                  title="Sin firma registrada: sus historias clínicas saldrán sin firma en el PDF"
                                >
                                  <PenLine className="w-3 h-3" />Sin firma
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <button onClick={() => setSelected(doc)}
                                  className="text-xs text-teal-600 hover:underline px-2 py-1">Ver</button>
                                <button onClick={() => setEditDoctor(doc)}
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-teal-600" title="Editar">
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleVerifyToggle(doc._id, !doc.isVerified)}
                                  disabled={actionLoading}
                                  className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                                    doc.isVerified ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-teal-50 text-teal-700 hover:bg-teal-100'
                                  } disabled:opacity-50`}>
                                  {doc.isVerified ? 'Desverificar' : 'Verificar'}
                                </button>
                                <button onClick={() => setDeleteTarget(doc)}
                                  className="p-1.5 rounded hover:bg-red-50 text-gray-300 hover:text-red-500" title="Desactivar">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">Página {pagination.page} de {pagination.pages} · {pagination.total} médicos</p>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => p - 1)} disabled={page <= 1 || loading}
                  className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setPage(p => p + 1)} disabled={page >= pagination.pages || loading}
                  className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Tab: Perfil incompleto */}
      {tab === 'incomplete' && <IncompleteTab onCompleted={handleIncompleteCompleted} />}

      {/* Drawers / Modals */}
      {selected && (
        <DoctorDrawer
          doctor={selected}
          onClose={() => setSelected(null)}
          onVerifyToggle={handleVerifyToggle}
          onEdit={(doc) => { setEditDoctor(doc); setSelected(null); }}
          onSignatureChanged={handleSignatureChanged}
          loading={actionLoading}
        />
      )}
      {showCreate && (
        <DoctorFormModal
          onClose={() => setShowCreate(false)}
          onSaved={(doc) => { handleSaved(doc, false); setShowCreate(false); }}
        />
      )}
      {editDoctor && (
        <DoctorFormModal
          editDoctor={editDoctor}
          onClose={() => setEditDoctor(null)}
          onSaved={(doc, isEdit) => { handleSaved(doc, isEdit); setEditDoctor(null); }}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          doctor={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          loading={actionLoading}
        />
      )}
    </div>
  );
}
